import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IChatHistory,
  IRequestFile,
  IResponseFile,
  Message,
  MessageType,
} from '../models/chat-message';
import {
  BehaviorSubject,
  Subject,
  map,
  switchMap,
  tap,
  of,
  catchError,
  throwError,
} from 'rxjs';
import { XanoClient } from '@xano/js-sdk/lib';
import { ERealtimeAction } from '@xano/js-sdk/lib/enums/realtime-action';
import { IConfig } from '../models/agency-config';
import { ISessionConfig, StartupService } from './startup.service';
import { environment } from '../../../environments/environment';
import {
  BotMessageType,
  AgencyType,
  UserMessageType,
  RealtimeMessageType,
  WEWEB_STAGING_URL,
  OC_DEV_URL,
  IframeMessageType,
} from '../utils/constants';
import { AuthService } from './auth.service';

const AGENCY_CONFIG_KEY = 'rh-agency-config';
const AGENCY_CONFIG_TIMESTAMP_KEY = 'rh-agency-config-timestamp';
const AGENCY_CONFIG_REQUEST_INTERVAL = 2; // Hours

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private baseUrl: string = environment.Xano.Endpoint;
  private token: string = environment.Xano.RealtimeToken;
  private env: string = environment.Xano.Env;
  private userId = 0;
  private isNewUser: boolean = false;
  private agencyId = 0;
  private lastMessageTimestamp = 0;
  private hostedDomain: string = '';
  private subscribedToIncomingMessages = false;

  newMessage = new Subject();
  conversation = new BehaviorSubject<Array<Message>>([]);
  configs?: IConfig;
  botEnabled = false;
  fileUploadRequired = false;
  uploadedFiles: IResponseFile[] = [];
  urlParams = '';
  sessionId = '';

  constructor(
    private httpClient: HttpClient,
    private startup: StartupService,
    private authService: AuthService,
  ) {}

  init() {
    return this.startup.sessionConfig.pipe(
      switchMap((sessionConfig) => this.getConfig(sessionConfig)),
      tap(() => {}),
    );
  }
  private getConfig(sessionConfig: ISessionConfig) {
    this.agencyId = sessionConfig.agencyId;
    this.sessionId = sessionConfig.sessionId;
    this.userId = sessionConfig.userId;
    this.hostedDomain = sessionConfig.hostedDomain;
    this.isNewUser = sessionConfig.userId == 0;
    this.urlParams = sessionConfig.urlParams;

    if (!sessionConfig.isNewAgency && !this.urlParams) {
      // Get Agency config in local storage
      const existingConfig = this.getExistingAgencyConfig();
      if (existingConfig) {
        this.configs = existingConfig;
        this.botEnabled = this.getBotEnabled(this.configs);

        return of({
          agencyId: this.agencyId,
          userId: this.userId,
          botEnabled: true,
        });
      }
    }

    // Get Agency config from api
    let url = this.getApiUrl(
      'api:bot_flows',
      `script/v3/agency_config_only?agencies_id=${this.agencyId}&channels_id=5`,
    );
    if (this.urlParams) {
      url += `&parameters=${this.urlParams}`;
    }

    return this.httpClient.get<any>(url).pipe(
      tap((data) => {
        data = this.updateAgencyConfigValues(data);
        localStorage.setItem(
          AGENCY_CONFIG_KEY,
          JSON.stringify(data.script.config),
        );
        localStorage.setItem(
          AGENCY_CONFIG_TIMESTAMP_KEY,
          new Date().getTime().toString(),
        );
        this.configs = data.script.config as IConfig;

        this.botEnabled = this.getBotEnabled(this.configs);
      }),
      map(() => ({
        agencyId: this.agencyId,
        userId: this.userId,
        botEnabled: this.botEnabled,
      })),
    );
  }
  getAgencyConfig() {
    // Get Agency config from api
    let url = this.getApiUrl(
      'api:bot_flows',
      `script/v3/agency_config?agencies_id=${this.agencyId}&bot_users_id=${this.userId}&channels_id=5&session_id=${this.sessionId}`,
    );
    if (this.urlParams) {
      url += `&parameters=${this.urlParams}`;
    }

    return this.httpClient.get<any>(url).pipe(
      tap((data) => {
        if (!data.bot_user.hasOwnProperty('id')) {
          this.userId = 0;
          return;
        }

        data = this.updateAgencyConfigValues(data);
        localStorage.setItem(
          AGENCY_CONFIG_KEY,
          JSON.stringify(data.script.config),
        );
        localStorage.setItem(
          AGENCY_CONFIG_TIMESTAMP_KEY,
          new Date().getTime().toString(),
        );
        this.configs = data.script.config as IConfig;
        this.userId = data.bot_user.id as number;
        this.sessionId = data.session_id as string;

        this.botEnabled = this.getBotEnabled(this.configs);
        if (this.userId > 0) this.subscribeToIncomingMessages();

        if (this.isNewUser) {
          this.saveConversation(data.flow_config);
        }

        if (data.token) {
          this.authService.setToken(data.token);
        }

        if (this.sessionId) {
          this.authService.setSessionId(this.sessionId);
        }
      }),
      map(() => ({
        agencyId: this.agencyId,
        userId: this.userId,
        botEnabled: this.botEnabled,
        sessionId: this.sessionId,
      })),
    );
  }

  private listenForIncomingMessages() {
    new XanoClient({
      instanceBaseUrl: this.baseUrl,
      realtimeConnectionHash: this.token,
    })
      .channel(`bot_messages/${this.userId}`)
      .on(
        ERealtimeAction.Event,
        (message: any) => this.handleIncomingMessage(message),
        (error: any) => console.log('Failed Message', error),
      );
  }

  getHistory() {
    // Skip history for a new user
    if (this.isNewUser) {
      return;
    }

    const url = this.getApiUrl(
      'api:SVP30IoD',
      `bot_channel_messages/v1/view_conversation?bot_users_id=${this.userId}`,
    );

    this.httpClient
      .get<IChatHistory>(url)
      .pipe(
        map((response) =>
          response.items
            .filter(
              (message) =>
                !this.getFilterMessageTypes().includes(
                  message.message_json.type,
                ),
            )
            .map(
              (message) =>
                new Message(
                  message.id,
                  message.message_json.bot_message
                    ? MessageType.Bot
                    : MessageType.User,
                  message.message_json,
                  message.message_json.created_at,
                  message.is_read,
                ),
            ),
        ),
      )

      .subscribe((result) => {
        var changeSet = result.filter(
          (d) => (d.createdAt ?? 0) > this.lastMessageTimestamp,
        );
        this.lastMessageTimestamp =
          changeSet.length > 0
            ? (changeSet[changeSet.length - 1].createdAt as number)
            : this.lastMessageTimestamp;
        //if first message is sent by user, remove it
        if (changeSet.length > 0) {
          const firstMessage = changeSet[0];
          if (firstMessage.type === MessageType.User) {
            changeSet.splice(0, 1);
          }
        }

        const conversation = [];
        conversation.push(...changeSet);
        this.conversation.next(conversation);
        this.newMessage.next(true);
      });
  }

  send(
    message_text: string,
    button_id: string = '',
    files: IResponseFile[] = [], //check
  ) {
    const url = this.getApiUrl('api:bot_flows', 'message/process_incoming_3');
    const requestData = {
      agencies_id: this.agencyId,
      bot_users_id: this.userId,
      channels_id: 5,
      button_id,
      message_text: message_text,
      files: [] as IResponseFile[],
    };

    if (files && files.length > 0) requestData.files = files;
    this.fileUploadRequired = false;

    // Add user message and typing message to display instant response
    this.addUserMessageToConversation({
      buttonId: button_id,
      messageText: message_text,
      files: files,
    });
    this.addTypingMessageToConversation();

    return this.httpClient.post<any>(url, requestData).pipe(
      tap((response) => {
        this.setUploadedFiles(response);
        if (response.flow_config) this.saveConversation(response.flow_config);
        else this.removeTypingMessageFromConversation();
        const attachmentAttribute = response.attributes.find(
          (item: any) => item.id === 41,
        );

        if (!attachmentAttribute) {
          this.uploadedFiles = [];
        }

        this.fileUploadRequired =
          response.attributes &&
          response.attributes.some(
            (item: any) => item.json_value && item.json_value.files_visible,
          );
        this.newMessage.next(true);
      }),
      catchError((err) => {
        this.removeTypingMessageFromConversation();
        return throwError(() => err);
      }),
      map((_) => true),
    );
  }

  private handleIncomingMessage(message: any) {
    if (this.isNewUser) this.isNewUser = false;

    if (message.payload.data.type) {
      if (message.payload.data.type === RealtimeMessageType.BOT_MESSAGE_NEW) {
        this.getHistory();
        window.parent.postMessage(
          {
            type: IframeMessageType.NEW_MESSAGE_NOTIFICATION,
          },
          '*',
        );
      }
    }
  }

  private saveConversation(messages: any[]) {
    const conversation = [];

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      if (this.getFilterMessageTypes().includes(message.type)) continue;

      conversation.push(
        new Message(
          message.id,
          message.bot_message ? MessageType.Bot : MessageType.User,
          message,
          message.created_at,
          false,
        ),
      );
    }

    this.conversation.next(conversation);
    if (conversation.length > 0) {
      this.lastMessageTimestamp = conversation[conversation.length - 1]
        .createdAt as number;
    }
  }

  private getFilterMessageTypes(): string[] {
    return [
      BotMessageType.REDIRECT,
      BotMessageType.SET_ATTRIBUTE,
      BotMessageType.INPUT,
      BotMessageType.PLUGIN,
    ];
  }

  uploadAttachments(files: IRequestFile[]) {
    const url = this.getApiUrl('api:bot_flows', 'files/v1/upload_attachments');
    const requestData = {
      agencies_id: this.agencyId,
      files: files,
    };

    return this.httpClient.post<any>(url, requestData).pipe(
      tap((response) => {
        return response;
      }),
    );
  }

  deleteAttachment(file_url: string) {
    if (!file_url) {
      return of(null);
    }
    const url = this.getApiUrl('api:bot_flows', 'files/v1/delete_attachment');
    const requestData = {
      agencies_id: this.agencyId,
      file_url: file_url,
    };

    const httpOptions = {
      body: requestData,
    };

    return this.httpClient.delete<any>(url, httpOptions).pipe(
      tap((response) => {
        return response;
      }),
    );
  }

  getExistingAgencyConfig() {
    const timestampVal =
      localStorage.getItem(AGENCY_CONFIG_TIMESTAMP_KEY) || '';
    if (timestampVal) {
      const timestampNow = new Date().getTime();
      const timestampOld = parseInt(timestampVal);
      if (
        timestampNow <
        timestampOld + AGENCY_CONFIG_REQUEST_INTERVAL * 60 * 60 * 1000
      ) {
        const configVal = localStorage.getItem(AGENCY_CONFIG_KEY) || '';
        if (configVal) {
          const agencyConfig = JSON.parse(configVal);
          return agencyConfig as IConfig;
        }
      }
    }
    return null;
  }

  getBotEnabled(configs: IConfig) {
    const currentDomain = this.getDomainWithoutProtocolAndWWW(
      this.hostedDomain,
    );

    const wewebStagingDomain =
      this.getDomainWithoutProtocolAndWWW(WEWEB_STAGING_URL);

    const ocDevDomain = this.getDomainWithoutProtocolAndWWW(OC_DEV_URL);

    // If new website_config list exists, pick the profile matching the current domain
    const websiteConfigs =
      (configs && configs.script && configs.script.website_config) || null;

    if (Array.isArray(websiteConfigs) && websiteConfigs.length > 0) {
      const profile = this.pickWebsiteProfile(websiteConfigs, currentDomain);
      const whitelisted = !!profile?.whitelisted;
      const whitelistDomain = this.getDomainWithoutProtocolAndWWW(
        profile?.whitelist_domain || '',
      );

      return (
        whitelisted &&
        (currentDomain == whitelistDomain ||
          currentDomain == wewebStagingDomain ||
          currentDomain == ocDevDomain) &&
        configs.script.type == AgencyType.RH
      );
    }

    // Fallback to legacy single-field structure
    const whitelistedDomain = this.getDomainWithoutProtocolAndWWW(
      configs.script.whitelist_domain,
    );

    return (
      configs.script.whitelisted &&
      (currentDomain == whitelistedDomain ||
        currentDomain == wewebStagingDomain ||
        currentDomain == ocDevDomain) &&
      configs.script.type == AgencyType.RH
    );
  }

  private pickWebsiteProfile(websiteConfigs: any[], currentDomain: string) {
    // Normalize domains and try exact match first
    const normalizedCurrent = currentDomain || '';

    for (const cfg of websiteConfigs) {
      const cfgDomain = this.getDomainWithoutProtocolAndWWW(
        cfg?.whitelist_domain || '',
      );
      if (cfgDomain && cfgDomain === normalizedCurrent) return cfg;
    }

    // Next, prefer an explicit active profile
    const active = websiteConfigs.find((c) => c && c.active_agency_website);
    if (active) return active;

    // Fallback to first profile
    return websiteConfigs[0];
  }

  private addUserMessageToConversation(message: any) {
    const msgTime = new Date().getTime();
    let type = UserMessageType.TEXT;
    if (message.buttonId) type = UserMessageType.BUTTON;
    else if (message.files && message.files.length > 0)
      type = UserMessageType.ATTACHMENTS;

    const userMessage: any = {
      id: 1,
      type: type,
      message_text: message.messageText,
      files: message.files,
      created_at: msgTime,
    };

    const conversation = [];
    conversation.push(
      new Message(userMessage.id, MessageType.User, userMessage, msgTime),
    );

    this.conversation.next(conversation);

    this.lastMessageTimestamp = msgTime;
  }

  private addTypingMessageToConversation() {
    const typingMessage = {
      id: 0,
      type: BotMessageType.TYPING,
      message_text: '',
    };
    const conversation = [];
    const msgTime = new Date().getTime();
    conversation.push(
      new Message(typingMessage.id, MessageType.User, typingMessage, msgTime),
    );

    this.conversation.next(conversation);

    this.lastMessageTimestamp = msgTime;
  }

  private removeTypingMessageFromConversation() {
    const removeMessage = {
      id: 2,
      type: BotMessageType.REMOVE_TYPING,
      message_text: '',
    };
    const conversation = [];
    const msgTime = new Date().getTime();
    conversation.push(
      new Message(removeMessage.id, MessageType.User, removeMessage, msgTime),
    );

    this.conversation.next(conversation);

    this.lastMessageTimestamp = msgTime;
  }

  private setUploadedFiles(response: any) {
    const { new_bot_channel_message } = response;
    if (
      new_bot_channel_message &&
      new_bot_channel_message.message_json &&
      new_bot_channel_message.message_json.files &&
      new_bot_channel_message.message_json.files.length > 0
    ) {
      this.uploadedFiles = new_bot_channel_message.message_json.files;
    }
  }

  private subscribeToIncomingMessages() {
    if (!this.subscribedToIncomingMessages) {
      this.listenForIncomingMessages();
      this.subscribedToIncomingMessages = true;
    }
  }

  clearCache() {
    localStorage.setItem(AGENCY_CONFIG_KEY, '');
    localStorage.setItem(AGENCY_CONFIG_TIMESTAMP_KEY, '');
  }

  private updateAgencyConfigValues(data: any) {
    let updatedData = { ...data };
    const { chat_window_start_state } = data?.script?.config;
    if (
      chat_window_start_state &&
      typeof chat_window_start_state === 'string'
    ) {
      updatedData.script.config.chat_window_start_state = parseInt(
        chat_window_start_state,
      );
    }

    return updatedData;
  }

  readMessages() {
    const messages = this.conversation.getValue();
    if (!messages || messages.length === 0) return of(true);

    const lastBotMessage = messages
      .slice()
      .reverse()
      .find((item) => item.type === MessageType.Bot);
    if (!lastBotMessage || lastBotMessage.isRead) {
      return of(true);
    }

    const unreadMessages = messages.filter((q) => q.isRead === false);
    for (let msg of unreadMessages) {
      msg.isRead = true;
    }

    const url = this.getApiUrl('api:bot_flows', 'message/read');
    const requestData = {
      agencies_id: this.agencyId,
      bot_users_id: this.userId,
    };

    return this.httpClient.put<any>(url, requestData).pipe(
      tap((response: any[]) => {}),
      catchError((err) => {
        return throwError(() => err);
      }),
      map((_) => true),
    );
  }

  getChatLink() {
    // If website_config list exists, prefer its bot_start_url for the current domain
    const websiteConfigs =
      (this.configs &&
        this.configs.script &&
        this.configs.script.website_config) ||
      null;

    let url = '';
    if (Array.isArray(websiteConfigs) && websiteConfigs.length > 0) {
      const profile = this.pickWebsiteProfile(
        websiteConfigs,
        this.getDomainWithoutProtocolAndWWW(this.hostedDomain),
      );
      url = profile?.bot_start_url || '';
    }

    // Fallback to legacy single-field
    if (!url) url = this.configs?.script?.bot_start_url || '';

    if (url) {
      return `${url}?rh-chat-window-start-state=1&rh-bot-users-id=${this.userId}`;
    }

    return '';
  }

  resetConversation(userId: number, isNewUser: boolean) {
    this.userId = userId;
    this.sessionId = '';
    this.isNewUser = false;
    this.lastMessageTimestamp = 0;
    this.conversation.next([]);
  }

  private getDomainWithoutProtocolAndWWW(url: string): string {
    if (!url) return '';

    // Add protocol if missing
    if (!/^https?:\/\//i.test(url)) {
      url = 'http://' + url;
    }

    try {
      const hostname: string = new URL(url).hostname.replace(/^www\./i, '');
      const parts: string[] = hostname.split('.');
      if (parts.length > 2) {
        return parts.slice(-3).join('.');
      }

      return parts.slice(-2).join('.');
    } catch (error) {
      return url;
    }
  }

  private getApiUrl(apiGroup: string, apiPath: string): string {
    let apiUrl = this.baseUrl;
    if (this.env) {
      apiUrl += `/${apiGroup}:${this.env}`;
    } else {
      apiUrl += `/${apiGroup}`;
    }
    apiUrl += `/${apiPath}`;
    return apiUrl;
  }
}
